import { BASIC_SUPPORT_GROUP_ID } from '@provision/conversation-constants';
import { SMS_SESSION_POLICY_DEFAULT_KEY } from '@provision/conversation-types';
import { useMemo } from 'react';
import { SelectMenuOption } from 'src/components/SelectMenu';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  useConversationAssigneeGroups,
  useConversationAssigneeGroupsOptions,
} from 'src/graphql';

type AssigneeGroupOption = ReturnType<typeof useConversationAssigneeGroupsOptions>[number];

/** Partners set the groups they have; the platform also sets "All other groups", its `default`. */
export function useSmsSessionGroupOptions(
  partnerId: number | null,
  partnerName: string | undefined,
) {
  const { loading } = useConversationAssigneeGroups(partnerId);
  const groups = useConversationAssigneeGroupsOptions(partnerId);

  const options = useMemo(
    () =>
      partnerId === PLATFORM_PARTNER_ID
        ? platformTenantOptions(groups)
        : partnerTenantOptions(groups, partnerName),
    [groups, partnerId, partnerName],
  );

  const defaultGroupKey = options.some((option) => option.value === BASIC_SUPPORT_GROUP_ID)
    ? BASIC_SUPPORT_GROUP_ID
    : (options[0]?.value ?? null);

  return { options, defaultGroupKey, loading };
}

function platformTenantOptions(groups: AssigneeGroupOption[]): SelectMenuOption[] {
  return [
    ...groups.map(toOption),
    { value: SMS_SESSION_POLICY_DEFAULT_KEY, title: 'All other groups' },
  ];
}

function partnerTenantOptions(
  groups: AssigneeGroupOption[],
  partnerName: string | undefined,
): SelectMenuOption[] {
  const platformGroups = groups.filter((group) => group.partnerId === PLATFORM_PARTNER_ID);
  const ownGroups = groups.filter((group) => group.partnerId !== PLATFORM_PARTNER_ID);
  if (!platformGroups.length || !ownGroups.length) {
    return groups.map(toOption);
  }

  return [
    ...platformGroups.map((group) => ({ ...toOption(group), group: 'Platform groups' })),
    ...ownGroups.map((group) => ({
      ...toOption(group),
      group: `${partnerName ?? 'Partner'} groups`,
    })),
  ];
}

function toOption(group: AssigneeGroupOption): SelectMenuOption {
  return { value: group.value, title: group.label };
}
